import { useCallback, useEffect, useState } from "react";
import { ownerService } from "../services/ownerService";
import { OwnerDashboardResponse } from "../types/owner.types";

interface UseOwnerDashboardResult {
  dashboard: OwnerDashboardResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const extractApiError = (error: unknown, fallback: string): string => {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    typeof (error as { response?: { data?: { detail?: string } } }).response?.data?.detail ===
      "string"
  ) {
    return (error as { response?: { data?: { detail?: string } } }).response?.data?.detail ?? fallback;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

export const useOwnerDashboard = (
  ownerName?: string,
): UseOwnerDashboardResult => {
  const [dashboard, setDashboard] = useState<OwnerDashboardResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ownerService.getDashboard(ownerName);
      setDashboard(data);
    } catch (apiError) {
      setError(
        extractApiError(
          apiError,
          "Could not load owner dashboard data.",
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [ownerName]);

  useEffect(() => {
    void load();
  }, [load]);

  return {
    dashboard,
    loading,
    error,
    refresh: load,
  };
};
